// src/services/retargeting/fbPixel.service.ts

/**
 * Facebook Pixel Service
 * Centralized Facebook Pixel tracking
 */

export enum FBPixelEventType {
    CHAT_STARTED = 'ChatStarted',
}

export interface FBPixelEventParams {
    value?: number;
    currency?: string;
    content_name?: string;
    content_category?: string;
    [key: string]: any;
}

declare global {
    interface Window {
        fbq: {
            (command: 'track', eventName: string, params?: any): void;
            (command: 'trackCustom', eventName: string, params?: any): void;
            (command: 'init', pixelId: string): void;
            (command: string, ...args: any[]): void;
            callMethod?: (...args: any[]) => void;
            queue?: any[];
            loaded?: boolean;
            version?: string;
        };
        _fbq?: any;
    }
}

class FBPixelService {
    private static instance: FBPixelService;
    private pixelId = '2700354070295529';
    private isInitialized = false;
    private scriptLoaded = false;

    private constructor() {}

    static getInstance(): FBPixelService {
        if (!FBPixelService.instance) {
            FBPixelService.instance = new FBPixelService();
        }
        return FBPixelService.instance;
    }

    initialize(): void {
        if (this.isInitialized) return;
        if (typeof window === 'undefined') return;

        this.loadFBPixel();
        this.isInitialized = true;
    }

    private loadFBPixel(): void {
        if (this.scriptLoaded) return;
        this.scriptLoaded = true;

        (function(f: any, b: any, e: string, v: string) {
            if (f.fbq) return;
            const n: any = f.fbq = function() {
                n.callMethod ?
                    n.callMethod.apply(n, arguments) : n.queue.push(arguments)
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = true;
            n.version = '2.0';
            n.queue = [];
            const t = b.createElement(e);
            t.async = true;
            t.src = v;
            const s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

        window.fbq('init', this.pixelId);
        window.fbq('track', 'PageView');

    }

    trackCustomEvent(
        eventType: FBPixelEventType | string,
        params: FBPixelEventParams = {}
    ): void {
        if (typeof window === 'undefined' || !window.fbq) {
            console.warn('Facebook Pixel not initialized');
            return;
        }

        try {
            const defaultParams: FBPixelEventParams = {
                value: 1.0,
                currency: 'CAD',
                ...params,
            };

            window.fbq('trackCustom', eventType, defaultParams);
        } catch (error) {
            console.error('Failed to track Facebook Pixel event:', error);
        }
    }

    trackViewContent(): void {
        if (typeof window === 'undefined' || !window.fbq) return;
        try {
            window.fbq('track', 'ViewContent');
        } catch (error) {
            console.error('Failed to track Facebook Pixel ViewContent:', error);
        }
    }

    trackChatStarted(): void {
        this.trackCustomEvent(FBPixelEventType.CHAT_STARTED);
    }
}

export const fbPixelService = FBPixelService.getInstance();
